import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, LOCALE_ID, signal } from '@angular/core';
import { Router } from '@angular/router';
import { RejectionReasonService } from '../../../../../services/rejection-reason/rejection-reason.service';
import { CityService } from '../../../../../services/city/city.service';
import { finalize } from 'rxjs';
import { ConfirmationDialog } from '../../../../../shared/ui/confirmation-dialog/confirmation-dialog';
import { SuccessfulNotification } from '../../../../../utils/successful-notification/successful-notification';
import { Pagination } from '../../../../../shared/ui/shared-tables/pagination/pagination';
import { Tables } from '../../../../../shared/ui/shared-tables/tables/tables';
import { Error } from '../../../../public-user-component/auth/shared/error/error';
import { Loading } from '../../../../../utils/loading/loading';
import { SubmitButton } from '../../../../../shared/ui/Buttons/submit-button/submit-button';
import { FilterSortingComponent } from '../../../../../shared/ui/shared-filters/filter-sorting-component/filter-sorting-component';
import { FilterDateComponent } from '../../../../../shared/ui/shared-filters/filter-date-component/filter-date-component';
import { FilterItemSearch } from '../../../../../shared/ui/shared-filters/filter-item-search/filter-item-search';

@Component({
  selector: 'app-governorates-list-component',
  imports: [
    ConfirmationDialog,
    SuccessfulNotification,
    Pagination,
    Tables,
    Error,
    Loading,
    SubmitButton,
    FilterSortingComponent,
    FilterDateComponent,
    FilterItemSearch,
  ],
  templateUrl: './governorates-list-component.html',
  styleUrl: './governorates-list-component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GovernoratesListComponent {
  private locale = inject(LOCALE_ID);
  private router = inject(Router);

  private destroyRef = inject(DestroyRef);
  private cityService = inject(CityService);
  ngOnInit() {
    this.loadGovernorates();
  }
  showSuccessNotification = signal(false);
  deleteErrorMessage = signal('');
  fromDateLabel = $localize`:@@adminCategoriesCreatedOnFrom:Created On (From)`;
  toDateLabel = $localize`:@@adminCategoriesCreatedOnTo:Created On (To)`;
  lastUpdatedOnFromLabel = $localize`:@@rejectionReasonLastUpdatedOnFrom:Last Updated On (From)`;
  lastUpdatedOnToLabel = $localize`:@@rejectionReasonLastUpdatedOnTo:Last Updated On (To)`;
  reasonNameLabel = $localize`:@@searchGovernorates:Search for governorate`;
  reasonNamePlaceholder = $localize`:@@searchGovernoratesPlaceholder:Search by governorate name`;
  Loading = $localize`:@@loading:Loading...`;
  //deleteSuccessMessage = $localize`:@@deleteRejectionReasonSuccess:Rejection reason deleted successfully`;
  showDeleteDialog = signal(false);
  onCancelDelete() {
    this.showDeleteDialog.set(false);
    this.deleteErrorMessage.set('');
    this.selectedGovernorateId.set(null);
  }
  onOrderByChange(value: string) {
    this.orderBy.set(value as 'TITLE' | 'CREATION_DATE' | 'LAST_MODIFICATION_DATE');

    this.currentPage.set(1);
    this.loadGovernorates();
  }
  onActionTrigger(event: { event: Event; actionId: number; entity: any }) {
    console.log('ACTION:', event);
    if (event.actionId === 3) {
      this.router.navigate(['/admin/cities', event.entity.id], {
        state: {
          governorateId: event.entity.id,
        },
      });

      return;
    }

    if (event.actionId === 1) {
      // this.selectedReasonId.set(event.entity.id);
      // this.selectedReasonTitle.set(event.entity.title);
      // this.deleteErrorMessage.set('');
      // this.showDeleteDialog.set(true);
    }
    if (event.actionId === 2) {
      this.selectedGovernorateId.set(event.entity.id);
      this.deleteErrorMessage.set('');
      this.showDeleteDialog.set(true);
    }
  }
  lastUpdatedOnFrom = signal<string | null>(null);
  lastUpdatedOnTo = signal<string | null>(null);
  fromDate = signal<string | null>(null);
  toDate = signal<string | null>(null);

  isArabic = computed(() => this.locale.startsWith('ar'));

  searchTerm = signal('');
  orderBy = signal<'TITLE' | 'CREATION_DATE' | 'LAST_MODIFICATION_DATE'>('CREATION_DATE');
  filters = signal({
    orderDir: 'DESC' as 'ASC' | 'DESC',
  });
  sortOptions = signal([
    {
      value: 'TITLE',
      labelEn: 'Title',
      labelAr: 'العنوان',
    },
    {
      value: 'CREATION_DATE',
      labelEn: 'Created Date',
      labelAr: 'تاريخ الإنشاء',
    },
    {
      value: 'LAST_MODIFICATION_DATE',
      labelEn: 'Last Modified Date',
      labelAr: 'آخر تعديل',
    },
  ]);
  loading = signal(false);
  errorGettingRejectionReasons = signal<string | null>(null);
  headerItems = computed(() => [
    { key: 'rowNumber', label: '#' },
    {
      key: 'title',
      label: this.isArabic() ? 'العنوان' : 'Title',
    },
    {
      key: 'number',
      label: this.isArabic() ? 'عدد المدن' : 'Number of Cities',
    },
    {
      key: 'createdOnBy',
      label: this.isArabic() ? 'تاريخ الإنشاء / بواسطة' : 'Created On / By',
    },
    {
      key: 'actions',
      label: '',
      type: 'actions',
    },
  ]);
  rows = signal<Record<string, any>[]>([]);
  emptyStateMessage = computed(() => {
    if (this.searchTerm().trim()) {
      return this.isArabic() ? 'لا توجد محافظات تطابق بحثك' : 'No governorates match your search';
    }

    const hasFilters =
      this.fromDate() !== null ||
      this.toDate() !== null ||
      this.lastUpdatedOnFrom() !== null ||
      this.lastUpdatedOnTo() !== null;

    if (hasFilters) {
      return this.isArabic() ? 'لا توجد محافظات تطابق الفلاتر المحددة' : 'No governorates match your selected filters';
    }

    return this.isArabic() ? 'لا توجد محافظات' : 'No governorates found';
  });
  loadGovernorates() {
    this.loading.set(true);
    this.errorGettingRejectionReasons.set(null);
    const subscription = this.cityService
      .getGovernoratesListService(this.isArabic() ? 'ar' : 'en', {
        pageNum: this.currentPage() - 1,
        pageSize: this.itemsPerPage(),
        orderBy: this.orderBy(),
        orderDir: this.filters().orderDir,
        quickSearch: this.searchTerm() || undefined,
        createdOnFrom: this.fromDate() || undefined,
        createdOnTo: this.toDate() || undefined,
        lastModifiedOnFrom: this.lastUpdatedOnFrom() || undefined,
        lastModifiedOnTo: this.lastUpdatedOnTo() || undefined,
      })
      .pipe(
        finalize(() => {
          this.loading.set(false);
        }),
      )
      .subscribe({
        next: (res: any) => {
          const list = res.list.map((item: any, index: any) => ({
            ...item,
            rowNumber: index + 1,
            title: this.isArabic() ? item.titleAr : item.titleEn,
            createdBy: item.createdBy?.fullName ?? '-',
            lastModifiedBy: item.lastModifiedBy?.fullName ?? '-',

            noOfCities: item,
          }));

          this.rows.set(list);
          this.totalItems.set(res.total);
        },
        error: (error) => {
          this.errorGettingRejectionReasons.set(
            this.isArabic()
              ? error.messageAr || 'حدث خطأ أثناء تحميل المحافظات'
              : error.messageEn || 'Error loading governorates.',
          );
        },
      });
    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }

  actions = signal([
    {
      id: 3,
      titleEn: 'View Cities',
      titleAr: 'عرض المدن',
    },
    {
      id: 1,
      titleEn: 'Edit Governorate',
      titleAr: 'تعديل المحافظة',
    },
    {
      id: 2,
      titleEn: 'Delete Governorate',
      titleAr: 'حذف المحافظة',
    },
  ]);
  currentPage = signal(1);
  itemsPerPage = signal(10);

  totalItems = signal(0);

  totalPages = computed(() => Math.ceil(this.totalItems() / this.itemsPerPage()));

  startItem = computed(() => (this.totalItems() === 0 ? 0 : (this.currentPage() - 1) * this.itemsPerPage() + 1));

  endItem = computed(() => Math.min(this.currentPage() * this.itemsPerPage(), this.totalItems()));

  pageNumbers = computed(() => Array.from({ length: this.totalPages() }, (_, index) => index + 1));
  onApplyFilters() {
    this.currentPage.set(1);
    this.loadGovernorates();
  }
  onResetFilters() {
    this.searchTerm.set('');
    this.fromDate.set(null);
    this.toDate.set(null);
    this.lastUpdatedOnFrom.set(null);
    this.lastUpdatedOnTo.set(null);

    this.orderBy.set('CREATION_DATE');

    this.filters.set({
      orderDir: 'DESC',
    });

    this.currentPage.set(1);

    this.loadGovernorates();
  }
  onGoToPage(page: number) {
    this.currentPage.set(page);
    this.loadGovernorates();
  }

  onNextPage() {
    if (this.currentPage() < this.totalPages()) {
      this.currentPage.update((page) => page + 1);
      this.loadGovernorates();
    }
  }

  onPreviousPage() {
    if (this.currentPage() > 1) {
      this.currentPage.update((page) => page - 1);
      this.loadGovernorates();
    }
  }

  toggleSortOrder() {
    this.filters.update((filters) => ({
      ...filters,
      orderDir: filters.orderDir === 'ASC' ? 'DESC' : 'ASC',
    }));

    this.currentPage.set(1);
    this.loadGovernorates();
  }

  selectedGovernorateId = signal<number | null>(null);
  deleteTitle = $localize`:@@governorateDeleteTitle:Delete Governorate`;

  deleteDescription = $localize`:@@governorateDeleteConfirmation:Are you sure you want to delete this governorate? This action cannot be undone`;

  confirmButton = signal($localize`:@@delete:Delete`);

  cancelButton = $localize`:@@cancelBtn:Cancel`;

  deleteSuccessMessage = $localize`:@@governorateDeleteSuccess:Governorate deleted successfully`;

  deleteInUseMessage = $localize`:@@governorateDeleteInUse:This governorate cannot be deleted because it is linked to a pharmacy or contains associated cities.`;

  onConfirmDelete() {
    const governorateId = this.selectedGovernorateId();

    if (governorateId === null) {
      return;
    }

    this.confirmButton.set(this.Loading);

    this.cityService.deleteGovernorate(governorateId).subscribe({
      next: () => {
        this.confirmButton.set($localize`:@@delete:Delete`);

        this.rows.update((rows) =>
          rows
            .filter((row) => row['id'] !== governorateId)
            .map((row, index) => ({
              ...row,
              rowNumber: index + 1,
            })),
        );

        this.totalItems.update((total) => Math.max(0, total - 1));

        this.showDeleteDialog.set(false);
        this.deleteErrorMessage.set('');
        this.selectedGovernorateId.set(null);

        this.showSuccessNotification.set(true);

        setTimeout(() => {
          this.showSuccessNotification.set(false);
        }, 3000);
      },

      error: (error: any) => {
        this.confirmButton.set($localize`:@@delete:Delete`);

        const backendMessage = this.isArabic() ? error?.messageAr : error?.messageEn;

        this.deleteErrorMessage.set(backendMessage?.trim() ? backendMessage : this.deleteInUseMessage);
      },
    });
  }
}
